import { useState } from "react";
import { toLocalInput } from "../../utils/datetimeLocal";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Printer, ClipboardList } from "lucide-react";
import {
  useAdminGetExamSessionQuery,
  useAdminGetSessionTimetableQuery,
  useAdminCreateTimetableEntryMutation,
  useAdminUpdateTimetableEntryMutation,
  useAdminDeleteTimetableEntryMutation,
  useAdminAttachDietToSessionMutation,
  useAdminDetachDietFromSessionMutation,
  useAdminGetDietsQuery,
  useAdminGetExamsQuery,
  useAdminGetCoursesByExamQuery,
  useAdminUpdateDietMutation,
} from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function fmt(d: string) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-NG", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
}

interface EntryForm { exam_id: string; exam_course_id: string; date: string; start_time: string; end_time: string; sort_order: number }
const blankEntry: EntryForm = { exam_id: "", exam_course_id: "", date: "", start_time: "9:30 AM", end_time: "12:30 PM", sort_order: 0 };

interface DietForm { title: string; status: string; applications_open_at: string; applications_close_at: string }
const blankDiet: DietForm = { title: "", status: "draft", applications_open_at: "", applications_close_at: "" };

export default function AdminExamSessionDetailsPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const sid = Number(sessionId);

  const { data: sessionData, isError: sessionError, refetch: refetchSession } = useAdminGetExamSessionQuery(sid);
  const { data: ttData, isLoading: ttLoading, isError: ttError, refetch: refetchTT } = useAdminGetSessionTimetableQuery(sid);
  const { data: allExams } = useAdminGetExamsQuery();
  const { data: allDiets } = useAdminGetDietsQuery();

  const [createEntry]  = useAdminCreateTimetableEntryMutation();
  const [updateEntry]  = useAdminUpdateTimetableEntryMutation();
  const [deleteEntry]  = useAdminDeleteTimetableEntryMutation();
  const [attachDiet]   = useAdminAttachDietToSessionMutation();
  const [detachDiet]   = useAdminDetachDietFromSessionMutation();
  const [updateDiet]   = useAdminUpdateDietMutation();

  // Reading a session and its timetable is exam.access, which opens this route.
  // Attaching a diet and editing timetable entries is exam.session.manage.
  const { can } = useAccess();
  const canManage = can.perm(PERMS.exam.sessionManage);

  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<EntryForm>(blankEntry);

  // Diet edit modal state
  const [showDietModal, setShowDietModal] = useState(false);
  const [editingDiet, setEditingDiet]     = useState<any | null>(null);
  const [dietForm, setDietForm]           = useState<DietForm>(blankDiet);
  const [dietError, setDietError]         = useState("");

  const { data: examCourses = [] } = useAdminGetCoursesByExamQuery(form.exam_id, { skip: !form.exam_id });
  const [error, setError]           = useState("");
  const [toast, setToast]           = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const session   = sessionData?.session ?? sessionData;
  const exams: any[]  = ttData?.exams ?? [];           // unique exams in this session
  const rows: any     = ttData?.rows ?? {};             // date -> slot -> subjects
  const linkedDiets: any[] = sessionData?.diets ?? [];
  const allDietsList: any[] = allDiets ?? [];
  const EXCLUDED_CODES = ["MRI", "PCI"];
  const allExamsList: any[] = (allExams ?? []).filter(
    (ex: any) => !EXCLUDED_CODES.includes((ex.code ?? "").toUpperCase())
  );

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const openCreate = () => { setForm(blankEntry); setEditId(null); setError(""); setShowForm(true); };
  const openEdit = (entry: any, examId: string) => {
    setForm({ exam_id: examId, exam_course_id: String(entry.subjects?.[examId]?.exam_course_id ?? ""), date: entry.date ?? "", start_time: entry.start_time, end_time: entry.end_time, sort_order: entry.sort_order ?? 0 });
    setEditId(entry.subjects?.[examId]?.entry_id ?? null);
    setError(""); setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.exam_id || !form.date || !form.exam_course_id) { setError("Exam, date, and course are required."); return; }
    setError("");
    try {
      if (editId !== null) {
        await updateEntry({ sessionId: sid, id: editId, body: form }).unwrap();
        showToast("Entry updated.");
      } else {
        await createEntry({ sessionId: sid, body: form }).unwrap();
        showToast("Entry added.");
      }
      setShowForm(false); refetchTT();
    } catch (err: any) { setError(err?.data?.message || "Failed to save."); }
  };

  const handleDelete = async (id: number) => {
    try { await deleteEntry({ sessionId: sid, id }).unwrap(); setConfirmDel(null); showToast("Deleted."); refetchTT(); }
    catch { showToast("Delete failed."); }
  };

  const handleAttach = async (dietId: string) => {
    try { await attachDiet({ sessionId: sid, diet_id: dietId }).unwrap(); showToast("Diet linked."); refetchSession(); }
    catch (err: any) { showToast(err?.data?.message || err?.error || "Failed to link diet."); }
  };

  const handleDetach = async (dietId: string) => {
    try { await detachDiet({ sessionId: sid, diet_id: dietId }).unwrap(); showToast("Diet unlinked."); refetchSession(); }
    catch { showToast("Failed."); }
  };

  const openEditDiet = (d: any) => {
    setEditingDiet(d);
    setDietForm({
      title: d.title ?? "",
      status: d.status ?? "draft",
      applications_open_at: toLocalInput(d.applications_open_at),
      applications_close_at: toLocalInput(d.applications_close_at),
    });
    setDietError("");
    setShowDietModal(true);
  };

  const handleSaveDiet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dietForm.title.trim()) { setDietError("Title is required."); return; }
    setDietError("");
    try {
      await updateDiet({ id: editingDiet.id, body: { title: dietForm.title, status: dietForm.status, applications_open_at: dietForm.applications_open_at || null, applications_close_at: dietForm.applications_close_at || null } }).unwrap();
      showToast("Diet updated.");
      setShowDietModal(false);
      refetchSession();
    } catch (err: any) { setDietError(err?.data?.message || "Failed to save."); }
  };

  const unlinkedDiets = allDietsList.filter(
    (d: any) => !linkedDiets.find((ld: any) => ld.id === d.id)
  );
  const sortedDates   = Object.keys(rows).sort();

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
      {toast && <div className="fixed top-6 right-6 z-50 px-4 py-3 rounded-2xl shadow-xl text-sm font-bold text-white bg-emerald-600">{toast}</div>}

      {(sessionError || ttError) && (
        <div className="bg-rose-50 border border-rose-200 rounded-[2rem] p-6 text-center">
          <p className="text-rose-600 font-black uppercase tracking-widest text-xs mb-1">Failed to load session data</p>
          <p className="text-rose-400 text-xs mb-3">
            {sessionError && "Session not found or database tables are missing (run php artisan migrate). "}
            {ttError && "Timetable table missing — run php artisan migrate."}
          </p>
          <button onClick={() => { refetchSession(); refetchTT(); }} className="px-4 py-2 bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-rose-700">Retry</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/exams/admin/sessions" className="p-2 rounded-xl hover:bg-slate-200 transition-all">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">{session?.title ?? "Exam Session"}</h1>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{session?.year} · Diet {session?.session_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/exams/admin/sessions/${sid}/attendance`}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all"
          >
            <ClipboardList className="w-3.5 h-3.5" /> Attendance List
          </Link>
          <Link
            to={`/exams/admin/sessions/${sid}/cards`}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all"
          >
            <Printer className="w-3.5 h-3.5" /> All Cards
          </Link>
          {canManage && <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Add Entry
          </button>}
        </div>
      </div>

      {/* Linked Diets */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6 space-y-3">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Linked Diets (exams running in this session)</p>
        <div className="flex flex-wrap gap-2">
          {linkedDiets.map((d: any) => (
            <div key={d.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-xl">
              <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">{d.exam?.code}: {d.title}</span>
              {canManage && <button onClick={() => openEditDiet(d)} className="text-emerald-400 hover:text-blue-500 ml-1" title="Edit diet">
                <Pencil className="w-3 h-3" />
              </button>}
              {canManage && <button onClick={() => handleDetach(d.id)} className="text-emerald-400 hover:text-rose-500 text-xs leading-none ml-0.5">×</button>}
            </div>
          ))}
          {linkedDiets.length === 0 && <p className="text-xs text-slate-400">No diets linked yet.</p>}
        </div>
        {unlinkedDiets.length > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex-shrink-0">Link existing diet:</p>
            <select
              onChange={e => { if (e.target.value) { handleAttach(e.target.value); e.target.value = ""; }}}
              className="border border-slate-200 rounded-xl px-3 py-1.5 text-xs bg-slate-50 outline-none"
              defaultValue=""
            >
              <option value="">Select diet…</option>
              {unlinkedDiets.map((d: any) => (
                <option key={d.id} value={d.id}>{d.exam?.code ?? "?"} — {d.title}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Timetable grid */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Timetable — {exams.length > 0 ? `${exams.length} exam${exams.length > 1 ? "s" : ""}` : "no entries yet"}
          </p>
        </div>

        {ttLoading && <div className="p-8 space-y-2">{[1,2].map(i=><div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse"/>)}</div>}

        {!ttLoading && sortedDates.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-slate-400 font-black uppercase tracking-widest text-xs mb-4">No timetable entries yet</p>
            {canManage && <button onClick={openCreate} className="px-5 py-2.5 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600">Add First Entry</button>}
          </div>
        )}

        {!ttLoading && sortedDates.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest w-32">Date</th>
                  <th className="px-4 py-3 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest w-32">Time</th>
                  {exams.map((ex: any) => (
                    <th key={ex.id} className="px-4 py-3 text-center text-[9px] font-black text-slate-700 uppercase tracking-widest">
                      {ex.code}<br/><span className="font-normal text-slate-400 normal-case">{ex.title}</span>
                    </th>
                  ))}
                  <th className="px-4 py-3 w-16"/>
                </tr>
              </thead>
              <tbody>
                {sortedDates.map(date =>
                  Object.entries(rows[date]).map(([slotKey, slot]: [string, any], slotIdx) => (
                    <tr key={`${date}-${slotKey}`} className="border-b border-slate-50 hover:bg-slate-50">
                      {slotIdx === 0 && (
                        <td className="px-4 py-3 font-black text-slate-700 align-top" rowSpan={Object.keys(rows[date]).length}>
                          {fmt(date)}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap font-bold text-slate-600">{slot.start_time}–{slot.end_time}</td>
                      {exams.map((ex: any) => (
                        <td key={ex.id} className="px-4 py-3 text-center text-slate-600 leading-snug">
                          {slot.subjects?.[ex.id]?.subject ?? <span className="text-slate-200">—</span>}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          {canManage && exams.map((ex: any) => slot.subjects?.[ex.id] && (
                            <button
                              key={ex.id}
                              onClick={() => openEdit({ date, ...slot }, ex.id)}
                              title={`Edit ${ex.code} entry`}
                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-orange-50 hover:text-orange-600 transition-all"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          ))}
                          {canManage && exams.map((ex: any) => slot.subjects?.[ex.id] && (
                            <button
                              key={`del-${ex.id}`}
                              onClick={() => setConfirmDel(slot.subjects[ex.id].entry_id)}
                              title={`Delete ${ex.code} entry`}
                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-rose-50 hover:text-rose-600 transition-all"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit entry modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">{editId !== null ? "Edit Entry" : "Add Timetable Entry"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Exam *</label>
                <select
                  value={form.exam_id}
                  onChange={e => setForm({...form, exam_id: e.target.value, exam_course_id: ""})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"
                >
                  <option value="">Select exam…</option>
                  {allExamsList.map((ex: any) => (
                    <option key={ex.id} value={ex.id}>{ex.code} — {ex.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Course / Paper *</label>
                <select
                  value={form.exam_course_id}
                  onChange={e => setForm({...form, exam_course_id: e.target.value})}
                  disabled={!form.exam_id}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none disabled:opacity-50"
                >
                  <option value="">{form.exam_id ? "Select course…" : "Select an exam first"}</option>
                  {examCourses.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3">
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"/>
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Start *</label>
                  <input type="text" placeholder="9:30 AM" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"/>
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">End *</label>
                  <input type="text" placeholder="12:30 PM" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"/>
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Order</label>
                  <input type="number" min="0" value={form.sort_order} onChange={e => setForm({...form, sort_order: Number(e.target.value)})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"/>
                </div>
              </div>
              {error && <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-orange-500 text-white hover:bg-orange-600">
                  {editId !== null ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Diet Edit Modal */}
      {showDietModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Edit Diet</h2>
              <button onClick={() => setShowDietModal(false)} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
            </div>
            <form onSubmit={handleSaveDiet} className="px-6 py-5 space-y-4">
              {editingDiet?.exam && (
                <div className="px-3 py-2 bg-slate-50 rounded-xl text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Exam: {editingDiet.exam.code} — {editingDiet.exam.title}
                </div>
              )}
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Title *</label>
                <input
                  type="text"
                  value={dietForm.title}
                  onChange={e => setDietForm({ ...dietForm, title: e.target.value })}
                  placeholder="e.g. TPC 2026 1st Diet"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"
                />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Status</label>
                <select
                  value={dietForm.status}
                  onChange={e => setDietForm({ ...dietForm, status: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Opens At</label>
                  <input
                    type="datetime-local"
                    value={dietForm.applications_open_at}
                    onChange={e => setDietForm({ ...dietForm, applications_open_at: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Closes At</label>
                  <input
                    type="datetime-local"
                    value={dietForm.applications_close_at}
                    onChange={e => setDietForm({ ...dietForm, applications_close_at: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"
                  />
                </div>
              </div>
              {dietError && <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">{dietError}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowDietModal(false)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-orange-500 text-white hover:bg-orange-600">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDel !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Delete Entry?</h3>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600">Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-rose-600 text-white hover:bg-rose-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
